import { withTimeout } from '../../utils/with-timeout'

/**
 * Native `isAvailable()` / `verifyIdentity()` can hang forever when the
 * Capacitor bridge never callbacks — missing plugin, Android `getActivity()`
 * NPE swallowed, BiometricManager deadlock, or iOS `LAContext.evaluatePolicy`
 * never invoking its reply (not-interactive / foreground-transition).
 *
 * 4s is the hang budget for both: `isAvailable` should return in milliseconds,
 * and the bug we ship against for verify is the OS sheet never appearing, not
 * a user slowly completing Face ID. Callers may still honor a late native
 * success so a slow passcode isn't lost.
 */
export const BIOMETRIC_AVAILABILITY_PROBE_MS = 4_000

/** Same hang budget as the availability probe — shared Capacitor-bridge hang. */
export const BIOMETRIC_VERIFY_PROBE_MS = BIOMETRIC_AVAILABILITY_PROBE_MS

export type NativeAvailabilityResult = {
  isAvailable: boolean
  biometryType: number
  errorCode?: number
}

export type BiometricAvailability = {
  available: boolean
  /** Human-friendly label, e.g. "Face ID", "Fingerprint". */
  label: string
  /** Plugin error code when unavailable (BiometricAuthError) — explains WHY. */
  errorCode?: number
  /**
   * True when the native probe timed out or threw — distinct from "device has
   * no biometrics enrolled", which is `available: false` without this flag.
   */
  probeFailed?: boolean
}

export function biometryLabel(type: number): string {
  // Mirrors BiometryType enum from @capgo/capacitor-native-biometric.
  switch (type) {
    case 1:
      return 'Touch ID'
    case 2:
      return 'Face ID'
    case 3:
      return 'Fingerprint'
    case 4:
      return 'Face unlock'
    case 5:
      return 'Iris unlock'
    case 6:
      return 'Biometrics'
    default:
      return 'Biometrics'
  }
}

/**
 * Run a native availability probe with a hang budget. Callers pass the plugin
 * `isAvailable()` (or a test double); a timeout or throw becomes `probeFailed`
 * so the UI can show error+retry instead of spinning forever.
 */
export async function settleBiometricAvailabilityProbe(
  probe: () => Promise<NativeAvailabilityResult>,
  timeoutMs: number = BIOMETRIC_AVAILABILITY_PROBE_MS,
): Promise<BiometricAvailability> {
  try {
    const result = await withTimeout(
      probe(),
      timeoutMs,
      'Timed out checking biometric support',
    )
    return {
      available: Boolean(result.isAvailable),
      label: biometryLabel(result.biometryType),
      errorCode: result.isAvailable ? undefined : result.errorCode,
    }
  } catch {
    return { available: false, label: 'Biometrics', probeFailed: true }
  }
}

/**
 * Time-box `verifyIdentity()` (or a test double). Timeout or throw surfaces to
 * the caller so the unlock sheet can leave "Waiting…" and show error + retry
 * instead of spinning forever. The underlying plugin call is not aborted.
 */
export async function settleBiometricVerify(
  verify: () => Promise<void>,
  timeoutMs: number = BIOMETRIC_VERIFY_PROBE_MS,
): Promise<void> {
  await withTimeout(verify(), timeoutMs, 'Timed out waiting for biometric verification')
}

/**
 * Generic UI hang budget (setTimeout + cancel handle) for non-interactive
 * probes, e.g. the settings App-lock row while `isAvailable()` runs.
 *
 * Do NOT use this to force-clear the unlock sheet's `checking` state while a
 * `verifyIdentity()` is pending: declaring the attempt dead while the OS
 * prompt is still up is what allowed concurrent native prompts to stack in
 * build 91 (Android re-verify loop, iOS freeze). Interactive verify timeouts
 * belong to the service layer (`settleBiometricVerify` + single-flight).
 */
export function startBiometricHangBudget(
  onHang: () => void,
  timeoutMs: number = BIOMETRIC_VERIFY_PROBE_MS,
): () => void {
  const timer = setTimeout(onHang, timeoutMs)
  return () => clearTimeout(timer)
}
